import React from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  CardListSkeleton,
  DashboardSkeleton,
  FeedSkeleton,
  FormSkeleton,
  OrderBunchSkeleton,
  SitesCardSkeleton,
  Skeleton,
  TableBodySkeleton,
} from './Skeleton';

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function PageHead({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="route-shell-head">
      <div>
        <h1 className="page-title">{title}</h1>
        {subtitle ? <p className="wf-page-subheading">{subtitle}</p> : null}
      </div>
    </div>
  );
}

function SearchBar({ placeholder }: { placeholder: string }) {
  return (
    <label className="route-shell-search">
      <SearchIcon />
      <input readOnly tabIndex={-1} placeholder={placeholder} aria-label={placeholder} />
    </label>
  );
}

function StatRow({ labels }: { labels: string[] }) {
  return (
    <div className="route-shell-stat-row" style={{ gridTemplateColumns: `repeat(${labels.length}, 1fr)` }}>
      {labels.map((label) => (
        <div key={label} className="route-shell-mini">
          <Skeleton width={36} height={20} />
          <span>{label}</span>
        </div>
      ))}
    </div>
  );
}

type TableRouteShellProps = {
  title: string;
  subtitle?: string;
  searchPlaceholder: string;
  columns: string[];
  rows?: number;
  stats?: string[];
};

/** Title, search, stat boxes, and column headers stay visible. Only table rows shimmer. */
export const TableRouteShell: React.FC<TableRouteShellProps> = ({
  title,
  subtitle,
  searchPlaceholder,
  columns,
  rows = 8,
  stats,
}) => (
  <div className="route-shell">
    <PageHead title={title} subtitle={subtitle} />
    {stats && stats.length > 0 ? <StatRow labels={stats} /> : null}
    <div className="route-shell-toolbar">
      <SearchBar placeholder={searchPlaceholder} />
    </div>
    <div className="route-shell-table-wrap">
      <table className="route-shell-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <TableBodySkeleton rows={rows} cols={columns.length} />
        </tbody>
      </table>
    </div>
  </div>
);

type ListRouteShellProps = {
  title: string;
  subtitle?: string;
  searchPlaceholder?: string;
  count?: number;
};

export const ListRouteShell: React.FC<ListRouteShellProps> = ({
  title,
  subtitle,
  searchPlaceholder,
  count = 6,
}) => (
  <div className="route-shell">
    <PageHead title={title} subtitle={subtitle} />
    {searchPlaceholder ? (
      <div className="route-shell-toolbar">
        <SearchBar placeholder={searchPlaceholder} />
      </div>
    ) : null}
    <CardListSkeleton count={count} />
  </div>
);

export const SitesRouteShell: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const inProject = Boolean(params.get('projectId'));
  const isStaffAdmin =
    user?.role === 'ADMIN' ||
    user?.role === 'SUPER_ADMIN' ||
    user?.email === 'superadmin@example.com';
  // Match the real page: client/agency, and anyone inside a project, never see catalog admin tools.
  const showAdminTools = isStaffAdmin && !inProject;

  return (
  <div className="route-shell">
    <PageHead title="Results for Your Search" />
    <div className="sites-shell-bar">
      <div className="sites-shell-row">
        <div className="sites-shell-main">
          <SearchBar placeholder="Search by URL or Site Name" />
          <div className="route-shell-filter">
            <span>DR</span>
            <input readOnly tabIndex={-1} placeholder="Min" aria-label="DR min" />
            <input readOnly tabIndex={-1} placeholder="Max" aria-label="DR max" />
          </div>
          <div className="route-shell-filter">
            <span>Traffic</span>
            <input readOnly tabIndex={-1} placeholder="Min" aria-label="Traffic min" />
            <input readOnly tabIndex={-1} placeholder="Max" aria-label="Traffic max" />
          </div>
          <div className="route-shell-filter">
            <span>Price</span>
            <input readOnly tabIndex={-1} placeholder="Min $" aria-label="Price min" />
            <input readOnly tabIndex={-1} placeholder="Max $" aria-label="Price max" />
          </div>
        </div>
        <button type="button" className="sites-shell-more" disabled>
          More filters
        </button>
      </div>
      <div className="sites-shell-row2">
        {showAdminTools && (
          <>
            <select className="route-shell-select sites-shell-wide" disabled defaultValue="" aria-label="Client">
              <option value="">Client</option>
            </select>
            <select className="route-shell-select sites-shell-wide" disabled defaultValue="" aria-label="Project">
              <option value="">Project</option>
            </select>
          </>
        )}
        <span className="sites-shell-spacer" />
        {showAdminTools && (
          <>
            <button type="button" className="sites-shell-btn" disabled>Export CSV</button>
            <button type="button" className="sites-shell-btn" disabled>Bulk Upload Sites</button>
            <button type="button" className="sites-shell-btn" disabled>Check All Sites</button>
            {user?.role === 'SUPER_ADMIN' || user?.email === 'superadmin@example.com' ? (
              <button type="button" className="sites-shell-btn" disabled>Update metrics</button>
            ) : null}
            <button type="button" className="sites-shell-btn sites-shell-btn--primary" disabled>Add Site</button>
          </>
        )}
      </div>
    </div>
    <SitesCardSkeleton count={6} />
  </div>
  );
};

export const OrdersRouteShell: React.FC = () => {
  const { user } = useAuth();
  const isClientOrAgency = user?.role === 'CLIENT' || user?.role === 'AGENCY';
  const reviewLabel = isClientOrAgency ? 'Order Processing' : 'For review';

  return (
  <div className="route-shell">
    <PageHead
      title="Guest Blog Orders"
      subtitle="Track publishing status and manage all guest blog orders"
    />
    <StatRow labels={['Total', 'Ready to Publish', reviewLabel, 'Need Correction', 'Completed']} />
    <div className="route-shell-chips">
      <button type="button" className="route-shell-chip" disabled>Ready to publish</button>
      <button type="button" className="route-shell-chip" disabled>{reviewLabel}</button>
      <button type="button" className="route-shell-chip" disabled>Completed</button>
    </div>
    <div className="route-shell-toolbar">
      <SearchBar placeholder="Search by Site URL, Domain" />
    </div>
    <OrderBunchSkeleton count={3} />
  </div>
  );
};

export const BacklinkRouteShell: React.FC = () => (
  <div className="route-shell">
    <div className="route-shell-head">
      <div>
        <h1 className="page-title">Backlink Health</h1>
        <p className="wf-page-subheading">
          Monitor the health of your live links across all clients and projects.
        </p>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="route-shell-chip" disabled>Import URLs</button>
        <button type="button" className="route-shell-chip" disabled style={{ background: '#4f46e5', color: '#fff', borderColor: '#4f46e5' }}>
          Check All
        </button>
      </div>
    </div>
    <div className="route-shell-stats">
      <div className="route-shell-stat route-shell-stat--neutral">
        <span>Total links</span>
        <Skeleton width={42} height={28} />
      </div>
      <div className="route-shell-stat route-shell-stat--green">
        <span>Healthy Links</span>
        <Skeleton width={42} height={28} style={{ background: 'rgba(255,255,255,0.35)' }} />
        <small>All Responding</small>
      </div>
      <div className="route-shell-stat route-shell-stat--red">
        <span>Errors Detected</span>
        <Skeleton width={42} height={28} style={{ background: 'rgba(255,255,255,0.35)' }} />
        <small>Links need attention</small>
      </div>
    </div>
    <div className="route-shell-alert">
      <Skeleton width={38} height={38} borderRadius={10} />
      <div style={{ flex: 1 }}>
        <Skeleton width="36%" height={14} />
        <Skeleton width="72%" height={12} style={{ marginTop: 8 }} />
      </div>
    </div>
    <div className="route-shell-toolbar">
      <SearchBar placeholder="Search by URL..." />
      <select className="route-shell-select" disabled defaultValue=""><option>All statuses</option></select>
      <select className="route-shell-select" disabled defaultValue=""><option>Type</option></select>
      <select className="route-shell-select" disabled defaultValue=""><option>Client</option></select>
      <select className="route-shell-select" disabled defaultValue=""><option>Project</option></select>
      <select className="route-shell-select" disabled defaultValue=""><option>All months</option></select>
      <select className="route-shell-select" disabled defaultValue=""><option>All Years</option></select>
    </div>
    <div className="route-shell-table-wrap">
      <table className="route-shell-table">
        <thead>
          <tr>
            {['Status', 'Live URL', 'Publisher', 'Type', 'Last Checked', 'Next Check'].map((column) => (
              <th key={column}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <TableBodySkeleton rows={8} cols={6} />
        </tbody>
      </table>
    </div>
  </div>
);

export const DashboardRouteShell: React.FC<{ title?: string; subtitle?: string }> = ({
  title = 'Dashboard',
  subtitle,
}) => (
  <div className="route-shell">
    <PageHead title={title} subtitle={subtitle} />
    <DashboardSkeleton />
  </div>
);

export const FormRouteShell: React.FC<{ title: string; subtitle?: string; fields?: number }> = ({
  title,
  subtitle,
  fields = 6,
}) => (
  <div className="route-shell">
    <PageHead title={title} subtitle={subtitle} />
    <FormSkeleton fields={fields} />
  </div>
);

export const FeedRouteShell: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
  <div className="route-shell">
    <PageHead title={title} subtitle={subtitle} />
    <div className="route-shell-toolbar">
      <SearchBar placeholder="Search activity…" />
    </div>
    <FeedSkeleton count={8} />
  </div>
);
